import { useState, useEffect, useRef } from "react";
import Axios from '@/shared/api/axios';
import Pagination from '@/components/ui/Pagination';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import LoadingButton from '@/components/ui/LoadingButton';
import IdolCard from '@/components/ui/IdolCard';
import IdolSelector from '@/components/ui/IdolSelector';
import { useTranslation } from 'react-i18next';

function BlockList() {
    const modalBlockSuccess = useRef(null);
    const modalUnblockConfirm = useRef(null);
    const statusModalRef = useRef(null);
    const { t } = useTranslation();

    const [selectedIdol, setSelectedIdol] = useState('');
    const [pendingBlockId, setPendingBlockId] = useState(null);
    const [isUnblocking, setIsUnblocking] = useState(false);
    const [currentStatusCode, setCurrentStatusCode] = useState('');
    const [pageParams, setPageParams] = useState({
        page: 1,
        limit: 10,
    });

    const [list, setList] = useState(null);
    const [total, setTotal] = useState(null);
    // 防亂序：快速切換偶像或換頁時，只套用最後一次請求的結果
    const fetchSeqRef = useRef(0);

    const getList = async (pa) => {
        const seq = ++fetchSeqRef.current;
        try {
            const res = await Axios.get("/idol/get-block-list", {
                params: pa
            });
            if (seq !== fetchSeqRef.current) return;
            switch (res.data.status_code) {
                case 'SYSTEM_1000':
                    setList(res.data.data);
                    setTotal(res.data.total);
                    break;
                default:
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                    break;
            }
        } catch (error) {
            if (seq !== fetchSeqRef.current) return;
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Get block list error:', error);
            }
        }
    };

    // 解除封鎖：送出後鎖住確認 modal，回應後才關閉並顯示結果
    const removeBlock = async (id) => {
        setIsUnblocking(true);
        try {
            const res = await Axios.post("/idol/delete-block", {
                block_id: id,
                idol_id: selectedIdol
            });
            switch (res.data.status_code) {
                case 'SYSTEM_1000':
                    modalBlockSuccess.current.show();
                    getList({ ...pageParams, idol_id: selectedIdol });
                    break;
                default:
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                    break;
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            }
        } finally {
            setIsUnblocking(false);
            modalUnblockConfirm.current?.hide();
        }
    };

    const handlePageChange = (number) => {
        const newPageParams = { ...pageParams, page: number, idol_id: selectedIdol };
        setPageParams({ ...pageParams, page: number });
        getList(newPageParams);
    };

    useEffect(() => {
        if (!selectedIdol) return;
        // 切換偶像時先清空前一個偶像的名單：解除封鎖是以目前選擇的偶像送出，
        // 名單若還是前一個偶像的，會拿錯的偶像去解除；載入失敗時也不該留著舊名單
        setList(null);
        setTotal(null);
        const newParams = { page: 1, limit: 10, idol_id: selectedIdol };
        setPageParams({ page: 1, limit: 10 });
        getList(newParams);
    }, [selectedIdol]);

    return (
        <>
            <h3 className="mb-4">{t('block_list')}</h3>
            <IdolSelector
                value={selectedIdol}
                onChange={setSelectedIdol}
                className="mb-4"
            />
            {list && list.length > 0 ? (
                <>
                    <div className="cards-grid mb-4">
                        {list.map(item => (
                            <IdolCard
                                key={item.id}
                                idol={{
                                    subscribed_idol_name: item.idol_name,
                                    subscribed_idol_is_official: item.idol_is_official,
                                    subscribed_idol_avatar_url: item.avatar_url,
                                    subscribed_idol_cover_url: item.cover_url,
                                }}
                                showSubscriptionInfo={false}
                                toolbar={{
                                    onUnblock: () => {
                                        setPendingBlockId(item.id);
                                        modalUnblockConfirm.current.show();
                                    }
                                }}
                            />
                        ))}
                    </div>
                    {total > pageParams.limit && (
                        <Pagination
                            total={total}
                            limit={pageParams.limit}
                            currentPage={pageParams.page}
                            onPageChange={handlePageChange}
                            scrollMode="window"
                        />
                    )}
                </>
            ) : (
                <p>{t('no_data')}</p>
            )}
            <MessageModal ref={modalBlockSuccess} status="success">
                <p>{t("idol_removed_blacklist")}</p>
            </MessageModal>
            <MessageModal ref={modalUnblockConfirm} status="warning" size="md" locked={isUnblocking}>
                <h5 className="mb-5">{t('unblock_confirm')}</h5>
                <div className="text-end">
                    <button type="button" className="btn btn-neutral rounded-pill me-3" onClick={() => modalUnblockConfirm.current.hide()} disabled={isUnblocking}>{t('cancel')}</button>
                    <LoadingButton className="btn btn-primary rounded-pill" onClick={() => removeBlock(pendingBlockId)} isLoading={isUnblocking}>{t('confirm')}</LoadingButton>
                </div>
            </MessageModal>

            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
        </>
    );
}

export default BlockList;
